import React from "react"
import { createErrorBoundary } from "#/utils/createErrorBoundary"
import { providerApi } from "./messagesHostApi"

// Host adapter for the messages package's unread dot: it hands the package its transport and nothing
// else. The PROVIDER half of the nav's indicator — the carrot half is the app's own, and
// `src/components/views/app/navigation/UnreadMessagesDot.tsx` decides which of the two gets to draw, so
// a member never sees two dots.
//
// Lazy for the same reason every other feature package is (Root.tsx, App.tsx) — a static import would
// put the dot, its query hook and the host context in the entry chunk. It pulls the `./unread-dot`
// subpath rather than the package root, whose index also exports `ProviderMessagesPanel`: through the
// root, consumers would fetch the whole panel on every screen just to decide whether to draw a dot.
//
// `fallback={null}` cannot flash — the dot renders nothing until the threads query resolves, so the
// deferred chunk only delays the indicator slightly.
const UnreadMessagesDot = React.lazy(() =>
  import("@internal/messages/unread-dot").then((m) => ({ default: m.UnreadMessagesDot }))
)

// A BOUNDARY, because this renders in the main nav on every authenticated screen and the app has none
// above it — `createErrorBoundary` is used once, and it wraps the non-authenticated routes. React
// answers an unhandled render error by unmounting the whole root, so without this a chunk that will not
// load takes the navigation down with it: a deploy rotating the asset out from under an open tab, a
// dropped connection, a CDN hiccup. `<Suspense>` does not help — it handles the pending promise, not a
// rejected one.
//
// OUTSIDE the Suspense boundary, so it catches the rejected import as well as anything the dot throws
// once it is running.
//
// The fallback renders NOTHING. An error affordance in the nav chrome would be far louder than the thing
// it is reporting on, and to a member "we could not find out" and "there is no unread mail" look the
// same. It is not silent to us, though: `createErrorBoundary` reports to Sentry with the origin tag
// below, so a chunk nobody can load still reaches triage.
const UnreadMessagesDotErrorBoundary = createErrorBoundary(() => null, "ProviderUnreadMessagesDot")

export function ProviderUnreadMessagesDot(): JSX.Element {
  return (
    <UnreadMessagesDotErrorBoundary>
      <React.Suspense fallback={null}>
        <UnreadMessagesDot getThreads={providerApi.getThreads} />
      </React.Suspense>
    </UnreadMessagesDotErrorBoundary>
  )
}
